import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/src/components/wrappers/Card";
import { fetch } from "@/src/lib/fetch";
import { Button } from "@/src/components/ui/button";
import { CheckBookingModal } from "./CheckBookingModal";
import { Booking } from "./model";
import { AlertTriangleIcon, CheckIcon, InfoIcon } from "lucide-react";

/**
 * Confirm booking (v14): derived from the booking's own state. A confirmation
 * row → read-only summary of what ops wrote when they checked it; none → the
 * CTA. The in-page Check-booking modal isn't built yet, so the CTA says where
 * checking runs today instead of pretending.
 */
export type Confirmation = {
  status: string | null;
  preBooking: boolean;
  confirmation: {
    xeroInvoiceNumber: string | null; invoiceTotal: string | null; invoiceCurrency: string | null; invoiceDueDate: string | null;
    xeroAccountCode: string | null; contractTemplate: string | null; paymentSource: string | null; opsUser: string | null;
    pipelineFrom: string | null; pipelineTo: string | null; brand: string | null; meta: Record<string, unknown> | null; at: string | null;
  } | null;
};
export const INBOX_PATH = "/payments-inbox";

export function useConfirmation(bookingId: string) {
  const [c, setC] = useState<Confirmation | null>(null);
  useEffect(() => {
    fetch<{ data: Confirmation }>(`/api/bookings/${bookingId}/confirmation`).then((r: any) => setC(r.data)).catch(() => setC(null));
  }, [bookingId]);
  return c;
}

export function ConfirmationSummary({ c, booking }: { c: Confirmation; booking?: Booking }) {
  const x = c.confirmation;
  if (!x) return null;
  const money = (v: string | null, ccy: string | null) => (v ? new Intl.NumberFormat("en-EN", { style: "currency", currency: ccy || "EUR", maximumFractionDigits: 0 }).format(Number(v) || 0) : null);
  const date = (v: string | null) => (v ? new Date(v).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null);
  const instalments = booking?.paymentSchedules?.filter((s: any) => String(s.payerType) === "Customer").length ?? 0;
  const meta = (x.meta ?? {}) as Record<string, any>;
  const cc = meta.ccSurchargePct != null ? `${meta.ccSurchargePct}% on ${meta.ccSurchargeBasis ?? "charter fee"}` : null;
  const brand = (x.brand ?? booking?.brand ?? "").toString().toLowerCase();
  // Same order and emphasis as the design's CONF_FIELDS: identifiers in mono.
  const fields: { k: string; v: string | null; mono?: boolean; sub?: string | null }[] = [
    { k: "Xero invoice", v: x.xeroInvoiceNumber, mono: true },
    { k: "Total", v: money(x.invoiceTotal, x.invoiceCurrency) },
    { k: "Due date", v: date(x.invoiceDueDate) },
    { k: "Charter account", v: x.xeroAccountCode, mono: true },
    { k: "Payments", v: instalments ? `${instalments} instalment${instalments === 1 ? "" : "s"}` : null },
    { k: "Contract", v: x.contractTemplate },
    { k: "CC surcharge", v: cc, sub: meta.ccSurchargeAmount != null ? money(String(meta.ccSurchargeAmount), x.invoiceCurrency) : null },
    { k: "Payment source", v: x.paymentSource },
    { k: "Checked by", v: x.opsUser ? `${x.opsUser}${x.at ? ` · ${date(x.at)}` : ""}` : null },
  ];
  return (
    <Card className="w-full">
      <div className="flex items-center gap-2">
        <CheckIcon className="h-4 w-4 text-green-700" />
        <h3 className="text-lg font-semibold">Booking already confirmed</h3>
        {brand && <span className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white ${brand === "anchor" ? "bg-[#e9695e]" : "bg-[#0000dc]"}`}>{brand}</span>}
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
        {fields.filter((f) => f.v).map((f) => (
          <div key={f.k}>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{f.k}</dt>
            <dd className={f.mono ? "font-mono text-sm font-semibold" : "text-sm font-semibold"}>{f.v}</dd>
            {f.sub && <dd className="text-xs text-slate-500">{f.sub}</dd>}
          </div>
        ))}
      </dl>
      {meta.notes && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"><AlertTriangleIcon className="h-4 w-4 shrink-0" />{String(meta.notes)}</p>
      )}
      <p className="mt-4 flex items-start gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
        <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>To re-confirm this booking, void the existing Xero invoice first and remove the confirmation row. Confirmation is idempotent on <code className="font-mono">hs_deal_id</code>.</span>
      </p>
    </Card>
  );
}

export function ConfirmTab({ c, bookingId }: { c: Confirmation | null; bookingId: string }) {
  const [open, setOpen] = useState(false);
  if (!c) return null;
  if (c.confirmation) return <ConfirmationSummary c={c} />;
  return (
    <Card className="w-full">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">Confirm booking</h3>
        <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">action required</span>
      </div>
      <p className="mt-2 text-sm text-slate-600">
        This booking sits at <b>New booking</b> and hasn't been checked. Checking verifies the supplier document against the deal, posts the supplier bill to Xero and moves the ticket to Booking checked — which unlocks the deposit and publishes the client booking page.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <Button onClick={() => setOpen(true)}>Check booking…</Button>
        {INBOX_PATH.startsWith("/") && <Link className="text-sm underline" to={INBOX_PATH}>or confirm from the payments inbox</Link>}
      </div>
      <CheckBookingModal open={open} onClose={() => setOpen(false)} bookingId={bookingId} onChecked={() => window.location.reload()} />
    </Card>
  );
}
